import gravityTax from '@nsftx/seven-gravity-tax-service';
import { ticketTypes } from './config';
import Helpers from '../helpers';

const getActiveSystems = ({ systems }) => systems.filter(item => item.active);

const getStakePerCombinations = ({ systems, betslipConfig, stake }) => {
  const selectedSystems = getActiveSystems({ systems });

  const tax = betslipConfig.taxes.payin && betslipConfig.taxes.payin.policy
    ? gravityTax.calculateTax(betslipConfig.taxes.payin.policy, stake)
    : false;

  const stakeTax = tax ? stake - tax.taxAmountRounded : stake;

  let combinations = 0;

  selectedSystems.forEach((item) => {
    combinations += item.combinations;
  });

  return stakeTax / combinations;
};

const getBankers = ({ bets }) => bets.filter(item => item.banker);

const getNonBankers = ({ bets }) => bets.filter(item => !item.banker);

const getSingleStake = ({ stake, bets, betslipConfig }) => {
  // const stake = parseFloat(this.stake);
  if (stake === 0 || bets.length === 0) return stake;
  const stakeTax = betslipConfig.taxes.payin && betslipConfig.taxes.payin.policy
    ? gravityTax.calculateTax(
      betslipConfig.taxes.payin.policy,
      stake,
    ).taxAmountRounded
    : false;
  const totalTax = stakeTax ? stake - stakeTax : stake;
  return totalTax / bets.length;
};

const getTotalOdds = ({ bets }) => {
  let total = 1;

  bets.forEach((bet) => {
    total *= parseFloat(bet.oddValue);
  });

  return bets.length > 0 ? total : 0;
};

const getSingleBonus = ({ bonusConfig }) => {
  if (bonusConfig && bonusConfig.length) {
    return _.find(bonusConfig, { maxBetCount: 1, minBetCount: 1 });
  }
  return false;
};

const getActiveBonus = ({ odds, bonusConfig }) => {
  let activeRule = false;

  if (bonusConfig) {
    for (let i = 0; i < bonusConfig.length; i += 1) {
      const rule = bonusConfig[i];

      let betSatisfaction = 0;

      for (let j = 0; j < odds.length; j += 1) {
        if (parseFloat(odds[j]) >= rule.minOdd) {
          betSatisfaction += 1;
        }
      }

      if (betSatisfaction >= rule.minBetCount
        && (rule.maxBetCount === 0 || betSatisfaction <= rule.maxBetCount)) {
        activeRule = rule;
      }
    }
  }

  return activeRule;
};

const calculateComboWinnings = ({ betslipConfig, stake, bets }) => {
  const tax = betslipConfig.taxes.payin && betslipConfig.taxes.payin.policy
    ? gravityTax.calculateTax(betslipConfig.taxes.payin.policy, stake)
    : false;

  const taxStake = tax ? stake - tax.taxAmountRounded : stake;

  const activeBonus = getActiveBonus(bets.map(bet => bet.oddValue));

  let max = taxStake * getTotalOdds({ bets });

  let bonus = 0;
  let bonusPercent = 0;

  if (activeBonus) {
    max *= (1 + (activeBonus.bonusPercent / 100));
    bonus = (max * (1 + (activeBonus.bonusPercent / 100)) - max) || 0;
    bonusPercent = activeBonus.bonusPercent || 0;
  }

  return {
    max: max || 0,
    bonus,
    bonusPercent,
  };
};

const calculateSingleWinnings = ({
  stake, bets, betslipConfig, bonusConfig,
}) => {
  let totalBonus = 0;
  const singleStake = getSingleStake({ stake, bets, betslipConfig });

  let sum = 0;

  const singleBonus = getSingleBonus({ bonusConfig });

  bets.forEach((bet) => {
    const oddValue = parseFloat(bet.oddValue);
    sum += oddValue;
    if (singleBonus && oddValue >= singleBonus.minOdd) {
      totalBonus += ((singleBonus.bonusPercent / 100) * oddValue * singleStake);
    }
  });

  sum *= singleStake;

  const winnings = {
    max: parseFloat(sum + totalBonus) || 0,
    bonus: totalBonus,
    bonusPercent: bets.length && singleBonus ? singleBonus.bonusPercent : 0,
  };

  return winnings;
};

const calculateSystemWinnings = ({
  bets, systems, betslipConfig, stake, bonusConfig,
}) => {
  const bankers = getBankers({ bets });
  const nonBankers = getNonBankers({ bets });
  const bankerOdds = bankers.map(item => parseFloat(item.oddValue));
  const combsList = nonBankers.map(item => parseFloat(item.oddValue));

  let bankersSum = 1;
  let systemsSum = 0;
  let systemsBonusSum = 0;
  let bonusPercent = 0;
  let systemSmallestSum;

  const stakePerCombination = getStakePerCombinations({ systems, betslipConfig, stake });

  for (let i = 0; i < bankerOdds.length; i += 1) {
    bankersSum *= parseFloat(bankerOdds[i]);
  }

  const activeSystems = getActiveSystems({ systems });

  for (let i = 0; i < activeSystems.length; i += 1) {
    const systemOdds = [];

    Helpers.getSystemCombsOdds(
      combsList,
      activeSystems[i].id,
      // eslint-disable-next-line
      (odds) => {
        let sum = 1;
        const { length } = odds;

        for (let j = 0; j < length; j += 1) {
          sum *= odds[j];
        }

        systemOdds.push(sum);

        // If there is bonus, do calculation of bonus
        if (bonusConfig && bonusConfig.length) {
          const bonusComb = odds.concat(bankerOdds);
          const activeBonusRule = getActiveBonus({ odds: bonusComb, bonusConfig });

          if (activeBonusRule) {
            if (bonusPercent < activeBonusRule.bonusPercent) {
              ({ bonusPercent } = activeBonusRule);
            }

            systemsBonusSum += (
              sum * bankersSum * (activeBonusRule.bonusPercent / 100) * stakePerCombination
            );
          }
        }
      },
    );

    activeSystems[i].maxWinning = 0;
    activeSystems[i].minWinning = Helpers.arrayMin(systemOdds);

    for (let id = 0; id < systemOdds.length; id += 1) {
      activeSystems[i].maxWinning += systemOdds[id] * stakePerCombination * bankersSum;
    }

    systemsSum += activeSystems[i].maxWinning;
  }

  return {
    min: systemSmallestSum,
    max: systemsSum + systemsBonusSum,
    bonus: systemsBonusSum,
    bonusPercent,
  };
};

const calculateWinnings = ({
  bets, stake, betslipConfig, ticketType, bonusConfig, systems,
}) => {
  let winnings;

  switch (ticketType) {
    case ticketTypes.COMBO:
      winnings = calculateComboWinnings({
        betslipConfig, stake, bets,
      });
      break;

    case ticketTypes.SYSTEM:
      winnings = calculateSystemWinnings({
        bets, systems, betslipConfig, stake, bonusConfig,
      });
      break;

    case ticketTypes.SINGLE:
      winnings = calculateSingleWinnings({
        stake, bets, betslipConfig, bonusConfig,
      });
      break;

    default:
      winnings = {
        max: 0,
        min: 0,
        bonus: 0,
      };
  }

  return winnings;
};

export {
  calculateWinnings,
  getSingleStake,
};
